import { FC, useState } from 'react';

// Third-party
import LoadingButton from '@mui/lab/LoadingButton';
import Grid from '@mui/material/Grid2';

// Hooks
import { useTranslation } from 'react-i18next';
import { FormProvider, useForm } from 'react-hook-form';
import { CustomSingleSelect } from '@mgm/common/components/base';
import { useAppSelector } from '../../../hooks/useAppStore';
import {
  useEndBreakAPI, useEndShiftAPI, useStartBreakAPI, useStartShiftAPI,
} from '../../../hooks/api/useClock';

// Redux-toolkit
import { getShiftDetails } from '../../../store/clock/selectors';

// Types
import { TClockAction } from '../type';
import { useClock } from '../hooks/useClock';
import { TLocation } from '../../../types';
import { getAuthenticateUser } from '../../../store/authenticate/selectors';

const ClockActions: FC<TClockAction> = ({ closeNav }) => {
  const {
    startCounter,
    clearTimer,
  } = useClock();
  const { t } = useTranslation('clock');

  const { personnel } = useAppSelector(getAuthenticateUser);

  const [location, setLocation] = useState(personnel.locations[0] as TLocation);

  const shiftDetails = useAppSelector(getShiftDetails);
  const { mutateAsync: startShiftMutate, isLoading: isStartShiftLoading } = useStartShiftAPI();
  const { mutateAsync: endShiftMutate, isLoading: isEndShiftLoading } = useEndShiftAPI();
  const { mutateAsync: startBreakMutate, isLoading: isStartBreakLoading } = useStartBreakAPI();
  const { mutateAsync: endBreakMutate, isLoading: isEndBreakLoading } = useEndBreakAPI();

  const methods = useForm({
    mode: 'all',
    values: {
      location,
    },
  });

  const startShift = async () => {
    const data = {
      location,
    };
    await startShiftMutate(data);
    startCounter();
    closeNav();
  };

  const endShift = async () => {
    const rsp = await endShiftMutate();

    if (rsp?.success) {
      clearTimer();
    }
    closeNav();
  };

  const takeBreak = async () => {
    await startBreakMutate();
    startCounter();
    closeNav();
  };

  const endBreak = async () => {
    await endBreakMutate();
    clearTimer();
    closeNav();
  };

  return (
    <Grid
      sx={{
        display: 'flex',
        alignItems: 'center',
      }}
      container
      spacing={2}
    >
      {!shiftDetails.started ? (
        <>
          <FormProvider {...methods}>
            <Grid
              size={{
                lg: 6,
                md: 6,
                xs: 12,
                sm: 6,
              }}
            >
              <div id="select-location">
                <CustomSingleSelect
                  value={location}
                  name="location"
                  isRequired
                  options={personnel.locations}
                  onChange={(location) => setLocation(location as TLocation)}
                />
              </div>
            </Grid>
          </FormProvider>
          <Grid size={{
            lg: 6,
            md: 6,
            xs: 12,
            sm: 6,
          }}
          >
            <LoadingButton
              id="clock-in"
              loading={isStartShiftLoading}
              onClick={startShift}
              fullWidth
              variant="contained"
            >
              {t('clockIn')}
            </LoadingButton>
          </Grid>
        </>
      ) : (
        <>
          <Grid size={{
            lg: 6,
            md: 6,
            xs: 6,
            sm: 6,
          }}
          >
            <LoadingButton
              loading={isStartBreakLoading || isEndBreakLoading}
              onClick={!shiftDetails.inBreak ? takeBreak : endBreak}
              fullWidth
              variant="contained"
            >
              {shiftDetails.inBreak ? t('backToWork') : t('takeABreak')}
            </LoadingButton>
          </Grid>
          <Grid size={{
            lg: 6,
            md: 6,
            xs: 6,
            sm: 6,
          }}
          >
            <LoadingButton
              loading={isEndShiftLoading}
              onClick={endShift}
              fullWidth
              variant="outlined"
            >
              {t('clockOut')}
            </LoadingButton>
          </Grid>
        </>
      )}
    </Grid>
  );
};

export {
  ClockActions,
};
